import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Icon,
  SearchInput,
  Avatar,
  Popover,
  type IconName,
} from "@/components/ui";
import { CardListSkeleton } from "@/components/feedback/Skeletons";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/hooks/useTheme";
import {
  useBulkNotificationAction,
  useNotificationInbox,
  useUnreadNotificationCount,
  useUpdateNotificationStatus,
} from "@/hooks/useNotifications";
import {
  NOTIFICATION_CREATED_EVENT,
  useRealtimeNotifications,
  type RealtimeNotification,
  type RealtimeResourceType,
} from "@/hooks/useRealtimeNotifications";
import type { EmailNotification } from "@/services/notifications";
import { relativeDate } from "@/components/templates/templateFormat";
import { HelpButton } from "./HelpButton";

/** Deep-link al recurso de una notificación push (S46 · Wave C, O7). */
function realtimeTarget(
  resourceType: RealtimeResourceType | undefined,
  resourceId: string | null | undefined,
): string {
  if (!resourceType || !resourceId) return "/notifications";
  switch (resourceType) {
    case "DOCUMENT":
      return `/documents/${resourceId}/edit`;
    case "MATTER":
      return `/matters/${resourceId}`;
    case "CLIENT":
      return `/clients?focus=${resourceId}`;
    default:
      return "/notifications";
  }
}

/** Deep-link al recurso de un item del inbox REST. */
function inboxTarget(notification: EmailNotification): string {
  if (notification.documentId)
    return `/documents/${notification.documentId}/edit`;
  if (notification.projectId) return `/matters/${notification.projectId}`;
  return "/notifications";
}

function categoryIcon(category: string | null | undefined): IconName {
  const value = (category ?? "").toLowerCase();
  if (value.includes("mention")) return "comment";
  if (value.includes("comment")) return "comment";
  if (value.includes("share") || value.includes("acl")) return "users";
  if (value.includes("assign")) return "check";
  if (value.includes("workflow") || value.includes("task")) return "play";
  if (value.includes("document")) return "doc";
  return "info";
}

interface ToastState {
  key: number;
  title: string;
  body?: string | null;
  target: string;
}

export function Topbar() {
  const navigate = useNavigate();
  const { isMobile, openDrawer } = useUI();
  const { user, hasPermission, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [search, setSearch] = useState("");
  const [notifOpen, setNotifOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const notifOpenRef = useRef(notifOpen);
  notifOpenRef.current = notifOpen;
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const toastTimerRef = useRef<number | null>(null);

  const notif = useNotificationInbox({ page: 0, size: 8 });
  const unreadCount = useUnreadNotificationCount();
  const bulk = useBulkNotificationAction();
  const updateStatus = useUpdateNotificationStatus();
  const realtime = useRealtimeNotifications();
  const items = notif.data?.content ?? [];
  const count = unreadCount.data?.count ?? 0;

  // Toast no intrusivo al recibir notification.created con el panel cerrado.
  useEffect(() => {
    const onCreated = (event: Event) => {
      const detail = (event as CustomEvent<RealtimeNotification>).detail;
      if (!detail || notifOpenRef.current) return;
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
      setToast({
        key: Date.now(),
        title: detail.title || detail.subject || "Nueva notificación",
        body: detail.body,
        target: realtimeTarget(detail.resourceType, detail.resourceId),
      });
      toastTimerRef.current = window.setTimeout(() => setToast(null), 6000);
    };
    window.addEventListener(NOTIFICATION_CREATED_EVENT, onCreated);
    return () => {
      window.removeEventListener(NOTIFICATION_CREATED_EVENT, onCreated);
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  function openNotification(item: EmailNotification) {
    if (item.status !== "READ" && item.status !== "ARCHIVED") {
      updateStatus.mutate({ id: item.id, status: "READ" });
    }
    setNotifOpen(false);
    navigate(inboxTarget(item));
  }

  return (
    <div
      className="h-15 sticky top-0 z-20 flex items-center gap-3 px-4 border-b border-border bg-bg"
      style={{ height: 60 }}
    >
      {isMobile && (
        <button
          onClick={openDrawer}
          className="p-1.5 text-text-2 rounded hover:bg-bg-2"
          aria-label="Abrir menú"
        >
          <Icon name="menu" size={18} />
        </button>
      )}

      <div className="flex-1 max-w-[520px] hidden sm:block">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Buscar documentos, cláusulas, proyectos…"
        />
      </div>
      <div className="flex-1 sm:hidden" />

      {hasPermission("projects.create") && (
        <Button
          variant="quiet"
          size="sm"
          icon="plus"
          onClick={() => navigate("/matters")}
        >
          Nuevo
        </Button>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:gap-2">
        <button
          onClick={toggleTheme}
          className="p-1.5 border border-border rounded text-text-2 hover:border-border-strong"
          aria-label="Cambiar tema"
          title={theme === "dark" ? "Tema claro" : "Tema oscuro"}
        >
          <Icon name="palette" size={16} />
        </button>

        <HelpButton />

        <div className="relative">
          <button
            ref={notificationButtonRef}
            onClick={() => setNotifOpen((v) => !v)}
            className="relative p-1.5 border border-border rounded text-text-2 hover:border-border-strong"
            aria-label={
              count > 0
                ? `Notificaciones · ${count} sin leer`
                : "Notificaciones"
            }
            aria-expanded={notifOpen}
          >
            <Icon name="bell" size={16} />
            <span aria-live="polite" className="sr-only">
              {count > 0 ? `${count} notificaciones sin leer` : ""}
            </span>
            {count > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-1.5 -right-1.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-danger px-1 text-[9px] font-semibold leading-none text-white ring-2 ring-bg"
              >
                {count > 9 ? "9+" : count}
              </span>
            )}
          </button>
          <Popover
            open={notifOpen}
            onClose={() => setNotifOpen(false)}
            align="end"
            anchorRef={notificationButtonRef}
            portal
            className="w-[360px] max-w-[calc(100vw-16px)] max-h-[480px] overflow-auto"
          >
            <div className="px-3 py-2 flex items-center justify-between">
              <div className="font-serif text-base font-medium">
                Notificaciones
              </div>
              <button
                onClick={() =>
                  bulk.mutate({
                    ids: items.map((item) => item.notification.id),
                    action: "MARK_READ",
                  })
                }
                disabled={items.length === 0 || bulk.isPending}
                className="text-xs text-muted hover:text-text disabled:opacity-50"
              >
                Marcar todas como leídas
              </button>
            </div>
            <div
              className="px-3 pb-2 text-[11px] text-muted"
              data-tour-id="ws-realtime-indicator"
            >
              {realtime.connected
                ? "Tiempo real conectado"
                : "Reconectando tiempo real…"}
            </div>
            {notif.isLoading && (
              <div className="p-3">
                <CardListSkeleton rows={3} />
              </div>
            )}
            {!notif.isLoading && items.length === 0 && (
              <div className="p-3 text-xs text-muted">Sin notificaciones.</div>
            )}
            {items.map((item) => {
              const n = item.notification;
              const unreadItem = n.status !== "READ" && n.status !== "ARCHIVED";
              return (
                <button
                  type="button"
                  key={n.id}
                  className={`flex w-full gap-2.5 p-2.5 rounded-md text-left hover:bg-bg-2 cursor-pointer ${
                    unreadItem ? "bg-primary-soft/30" : ""
                  }`}
                  onClick={() => openNotification(n)}
                >
                  <div
                    className={`w-7 h-7 rounded-full grid place-items-center shrink-0 ${
                      n.priority === "HIGH" || n.priority === "URGENT"
                        ? "bg-danger-soft text-danger"
                        : "bg-bg-2 text-text-2"
                    }`}
                  >
                    <Icon name={categoryIcon(n.category)} size={13} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold truncate">
                        {n.subject}
                      </span>
                      {unreadItem && (
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                          aria-label="Sin leer"
                        />
                      )}
                    </div>
                    <div className="text-[11px] text-text-2 mt-0.5 line-clamp-2">
                      {n.bodyText ?? n.category}
                    </div>
                    <div className="text-[10px] text-muted mt-1">
                      {relativeDate(n.createdAt)}
                    </div>
                  </div>
                </button>
              );
            })}
            <div className="border-t border-border p-2">
              <button
                type="button"
                className="w-full rounded-md px-2 py-1.5 text-center text-xs text-primary hover:bg-bg-2"
                onClick={() => {
                  setNotifOpen(false);
                  navigate("/notifications");
                }}
              >
                Ver todas las notificaciones
              </button>
            </div>
          </Popover>
        </div>

        {hasPermission("ai.chat.execute") && (
          <Button
            variant="quiet"
            size="sm"
            icon="spark"
            className="hidden lg:inline-flex"
            data-tour-id="topbar-ai-button"
            onClick={() => navigate("/ai")}
          >
            Asistente
          </Button>
        )}

        <div className="relative">
          <button
            ref={accountButtonRef}
            type="button"
            onClick={() => setAccountOpen((v) => !v)}
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45"
            aria-label="Cuenta de usuario"
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            title={user?.displayName ?? user?.email ?? "Mi cuenta"}
            data-tour-id="topbar-account-button"
          >
            <Avatar name={user?.displayName ?? user?.email ?? "?"} size={32} />
          </button>
          <Popover
            open={accountOpen}
            onClose={() => setAccountOpen(false)}
            align="end"
            anchorRef={accountButtonRef}
            portal
            className="w-[240px]"
          >
            <div className="border-b border-border px-3 py-2.5">
              <div className="truncate text-sm font-semibold">
                {user?.displayName ?? user?.email}
              </div>
              {user?.email && user?.displayName && (
                <div className="truncate text-[11px] text-muted">
                  {user.email}
                </div>
              )}
            </div>
            <div className="p-1" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAccountOpen(false);
                  navigate("/account");
                }}
                className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-text-2 hover:bg-bg-2 hover:text-text"
              >
                <Icon name="user" size={15} />
                Mi cuenta
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAccountOpen(false);
                  logout().then(() => navigate("/login", { replace: true }));
                }}
                className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-danger hover:bg-danger-soft"
              >
                <Icon name="logout" size={15} />
                Cerrar sesión
              </button>
            </div>
          </Popover>
        </div>
      </div>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-50 w-[320px] rounded-lg border border-border bg-elevated p-3 shadow-lg"
        >
          <div className="flex items-start gap-2.5">
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary-soft text-primary">
              <Icon name="bell" size={13} />
            </div>
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              onClick={() => {
                const target = toast.target;
                setToast(null);
                navigate(target);
              }}
            >
              <div className="truncate text-xs font-semibold text-text">
                {toast.title}
              </div>
              {toast.body && (
                <div className="mt-0.5 line-clamp-2 text-[11px] text-text-2">
                  {toast.body}
                </div>
              )}
            </button>
            <button
              type="button"
              className="shrink-0 rounded p-0.5 text-muted hover:bg-bg-2 hover:text-text"
              aria-label="Cerrar notificación"
              onClick={() => setToast(null)}
            >
              <Icon name="close" size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
